import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import tw from 'twin.macro'
import {
  MobileHamburgerMajor,
  ChatMajor,
  ChevronRightMinor,
  CircleRightMajor,
  MobileCancelMajor,
} from '@shopify/polaris-icons'
import { Typography } from '@mui/material'
import { Button } from '../shared/Button'
import { LogoutModal } from './LogoutModal'
import { useSelector } from 'react-redux'
import { getContractorPortalPartners } from 'src/selectors/contractorPortal'
import { SwitchPartnerModal } from './SwitchPartnerModal'
import { show as showIntercom } from '@intercom/messenger-js-sdk'
import { useAuthTokens } from 'src/hooks/useAuthTokens'
import Logo from 'src/assets/images/gigsafe-logo-black.svg'
import RecollectIcon from 'src/assets/icons/recollect.svg'
import { GigSafeMemberBenefitPortal } from '../shared/GigSafeMemberBenefitPortal'

interface Props {
  modalContainerRef: React.RefObject<HTMLDivElement>
  flow?: 'client' | 'contractorPortal'
  variant?: 'light' | 'dark'
}

const MenuComponent = ({ flow, variant = 'dark', modalContainerRef }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [isSwitchPartnerOpen, setIsSwitchPartnerOpen] = useState(false)

  const { authToken: clientAuthToken, contractorPortalAuthToken } = useAuthTokens()

  const contractorPortalPartners = useSelector(getContractorPortalPartners)

  const authToken = useMemo(() => {
    if (flow === 'client') {
      return clientAuthToken
    }

    if (flow === 'contractorPortal') {
      return contractorPortalAuthToken
    }

    return null
  }, [flow, clientAuthToken, contractorPortalAuthToken])

  const showSwitchPartner = useMemo(
    () => flow === 'contractorPortal' && contractorPortalPartners.length > 1,
    [flow, contractorPortalPartners.length],
  )

  const menuTestIdPrefix = useMemo(() => {
    if (flow === 'contractorPortal') {
      return 'contractors-list'
    }

    if (flow === 'client') {
      return 'customer-interface'
    }

    return 'sign-in'
  }, [flow])

  const openMenu = useCallback(() => {
    setIsMenuOpen(true)
  }, [])

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const openLogout = useCallback(() => {
    setIsLogoutOpen(true)
  }, [])

  const closeLogout = useCallback(() => {
    setIsLogoutOpen(false)
  }, [])

  const handleGetHelp = useCallback(() => {
    showIntercom()
  }, [])

  const openSwitchPartner = useCallback(() => {
    setIsSwitchPartnerOpen(true)
  }, [])

  const closeSwitchPartner = useCallback(() => {
    setIsSwitchPartnerOpen(false)
    setIsMenuOpen(false)
  }, [])

  return (
    <div>
      {authToken && (
        <Button
          leftIcon={
            <MobileHamburgerMajor
              css={[tw`mx-1`, variant === 'light' ? tw`fill-primary-500` : tw`fill-shades-0`]}
              width={20}
              height={20}
            />
          }
          onClick={openMenu}
          size="medium"
          color={variant === 'light' ? 'light' : 'primary'}
          data-testid={`${menuTestIdPrefix}-menu-btn`}
        />
      )}

      {modalContainerRef.current &&
        createPortal(
          <Fragment>
            {isMenuOpen && (
              <div css={tw`absolute inset-0 z-50 flex flex-col`} onClick={closeMenu}>
                <div css={tw`absolute inset-0 bg-shades-1000 bg-opacity-50`} />
                <div
                  css={tw`relative z-10 w-full h-full flex flex-col`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div css={tw`flex px-4 py-3 justify-between items-center bg-shades-0`}>
                    <img src={Logo} />
                    <Button
                      leftIcon={<MobileCancelMajor css={tw`mx-1`} width={20} height={20} />}
                      onClick={closeMenu}
                      color="grey"
                      size="medium"
                      data-testid={`${menuTestIdPrefix}-menu-close-btn`}
                    />
                  </div>
                  <div css={tw`bg-shades-50 p-4 h-fit flex-1 flex flex-col`}>
                    <div css={tw`bg-shades-0 rounded-2xl overflow-hidden`}>
                      {showSwitchPartner && (
                        <div
                          css={tw`p-4 flex items-center justify-between hover:bg-shades-100 active:bg-shades-200 cursor-pointer`}
                          onClick={openSwitchPartner}
                          data-testid={`${menuTestIdPrefix}-switch-partner`}
                        >
                          <div css={tw`flex items-center`}>
                            <div
                              css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50`}
                            >
                              <img src={RecollectIcon} />
                            </div>
                            <Typography fontWeight={600} css={tw`ml-4`}>
                              Switch partner
                            </Typography>
                          </div>
                          <ChevronRightMinor css={tw`fill-shades-300`} width={20} height={20} />
                        </div>
                      )}
                      <div
                        css={tw`p-4 flex items-center justify-between hover:bg-shades-100 active:bg-shades-200 cursor-pointer`}
                        onClick={handleGetHelp}
                        data-testid={`${menuTestIdPrefix}-get-help`}
                      >
                        <div css={tw`flex items-center`}>
                          <div
                            css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50`}
                          >
                            <ChatMajor width={20} height={20} />
                          </div>
                          <Typography fontWeight={600} css={tw`ml-4`}>
                            Get Help
                          </Typography>
                        </div>
                        <ChevronRightMinor css={tw`fill-shades-300`} width={20} height={20} />
                      </div>
                      {authToken && (
                        <div
                          css={tw`p-4 flex items-center justify-between hover:bg-shades-100 active:bg-shades-200 cursor-pointer`}
                          onClick={openLogout}
                          data-testid={`${menuTestIdPrefix}-log-out`}
                        >
                          <div css={tw`flex items-center`}>
                            <div
                              css={tw`h-11 w-11 rounded-[22px] items-center justify-center flex bg-shades-50`}
                            >
                              <CircleRightMajor width={20} height={20} />
                            </div>
                            <Typography fontWeight={600} css={tw`ml-4`}>
                              Log out
                            </Typography>
                          </div>
                          <ChevronRightMinor css={tw`fill-shades-300`} width={20} height={20} />
                        </div>
                      )}
                    </div>
                    <div css={tw`my-4`}>
                      <GigSafeMemberBenefitPortal />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <LogoutModal flow={flow} isOpen={isLogoutOpen} onClose={closeLogout} />
            <SwitchPartnerModal isOpen={isSwitchPartnerOpen} onClose={closeSwitchPartner} />
          </Fragment>,
          modalContainerRef.current,
        )}
    </div>
  )
}

export const Menu = memo(MenuComponent)
